import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FiAlertCircle, FiArrowRight, FiArrowUp, FiBarChart2,
  FiBriefcase, FiCpu, FiFileText, FiLoader, FiPlus,
  FiRefreshCw, FiSearch, FiShield, FiTrendingDown, FiUpload
} from 'react-icons/fi';
import { useClient } from '../context/ClientContext';
import { aiService } from '../services/aiService';
import {
  getQuickBooksAccounts,
  getProfitYoAccountsForQuickBooks
} from '../services/quickbooksService';
import { getWaveAccounts } from '../services/waveService';
import CsvImportModal from '../components/assistant/CsvImportModal';
import QuickBooksImportModal from '../components/assistant/QuickBooksImportModal';
import WaveImportModal from '../components/assistant/WaveImportModal';
import '../css/Assistant.css';

const MAX_HISTORY = 10;
const MAX_MESSAGE_LENGTH = 4000;

const SUGGESTIONS = [
  {
    title: 'Review transactions',
    description: 'Find items that may need attention',
    prompt: 'Review the available transactions for this client. Identify uncategorized entries, potential issues, and items needing review. Do not invent transactions.',
    icon: FiSearch
  },
  {
    title: 'Profit & loss',
    description: 'Understand financial performance',
    prompt: 'Summarize the available current-year profit and loss for this client, including revenue, expenses, and net income. Clearly state when data is missing.',
    icon: FiBarChart2
  },
  {
    title: 'Biggest expenses',
    description: 'Understand spending patterns',
    prompt: 'Based only on available financial records, identify this client’s largest expense categories and explain what they mean.',
    icon: FiTrendingDown
  },
  {
    title: 'Cash flow',
    description: 'Review available cash movements',
    prompt: 'Explain this client’s available cash flow information and highlight any limitations in the data.',
    icon: FiRefreshCw
  }
];

const IMPORT_OPTIONS = [
  { id: 'csv', label: 'CSV file', description: 'Import accounting records from CSV', icon: FiFileText },
  { id: 'quickbooks', label: 'QuickBooks', description: 'Review accounts from connected QuickBooks', icon: FiUpload },
  { id: 'wave', label: 'Wave', description: 'Choose a connected Wave account', icon: FiUpload }
];

const makeMessage = (role, content) => ({
  id: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
  role,
  content
});

function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  return (
    <motion.article
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className={`flex w-full min-w-0 gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      {!isUser && <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white"><FiCpu /></div>}
      <div className={isUser ? 'min-w-0 max-w-[85%] sm:max-w-[75%]' : 'min-w-0 max-w-[calc(100%-3rem)] flex-1'}>
        {!isUser && <p className="mb-2 text-[11px] font-semibold text-slate-500">ProfitYo AI</p>}
        {isUser ? (
          <div className="whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-slate-900 px-4 py-3 text-left text-[13px] leading-6 text-white">{message.content}</div>
        ) : (
          <div className="min-w-0 overflow-hidden rounded-2xl rounded-bl-md border border-slate-200 bg-white px-4 py-4 text-left text-[13px] leading-7 text-slate-700 shadow-sm sm:px-5">
            <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml components={{
              h1: ({ node, ...props }) => <h1 className="mb-4 mt-1 break-words text-xl font-bold leading-snug text-slate-900" {...props} />,
              h2: ({ node, ...props }) => <h2 className="mb-3 mt-5 break-words text-lg font-bold leading-snug text-slate-900 first:mt-0" {...props} />,
              h3: ({ node, ...props }) => <h3 className="mb-2 mt-4 break-words text-sm font-bold text-slate-900 first:mt-0" {...props} />,
              p: ({ node, ...props }) => <p className="mb-3 whitespace-pre-wrap break-words leading-7 last:mb-0" {...props} />,
              strong: ({ node, ...props }) => <strong className="font-semibold text-slate-900" {...props} />,
              em: ({ node, ...props }) => <em className="italic text-slate-700" {...props} />,
              ul: ({ node, ...props }) => <ul className="mb-4 list-disc space-y-1.5 pl-5 marker:text-slate-400 last:mb-0" {...props} />,
              ol: ({ node, ...props }) => <ol className="mb-4 list-decimal space-y-1.5 pl-5 marker:text-slate-500 last:mb-0" {...props} />,
              li: ({ node, ...props }) => <li className="break-words pl-1 leading-6" {...props} />,
              table: ({ node, ...props }) => <div className="my-4 w-full max-w-full overflow-x-auto rounded-xl border border-slate-200"><table className="w-full min-w-[640px] border-collapse text-left text-xs" {...props} /></div>,
              thead: ({ node, ...props }) => <thead className="bg-slate-50" {...props} />,
              tbody: ({ node, ...props }) => <tbody className="divide-y divide-slate-100 bg-white" {...props} />,
              tr: ({ node, ...props }) => <tr className="transition-colors hover:bg-slate-50/80" {...props} />,
              th: ({ node, ...props }) => <th className="whitespace-nowrap border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600 [&[align=right]]:text-right" {...props} />,
              td: ({ node, ...props }) => <td className="px-4 py-3 align-top leading-5 text-slate-700 [&[align=right]]:text-right [&[align=right]]:tabular-nums" {...props} />,
              code: ({ node, className, ...props }) => <code className={`break-words rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-800 ${className || ''}`} {...props} />,
              pre: ({ node, ...props }) => <pre className="my-4 max-w-full overflow-x-auto rounded-xl bg-slate-100 p-4 text-xs leading-6 text-slate-800" {...props} />,
              blockquote: ({ node, ...props }) => <blockquote className="my-4 border-l-2 border-emerald-500 bg-slate-50 px-4 py-3 text-slate-600" {...props} />,
              hr: ({ node, ...props }) => <hr className="my-5 border-slate-200" {...props} />,
              a: ({ node, href, children, ...props }) =>
                typeof href === 'string' && /^https?:\/\//i.test(href)
                  ? <a href={href} target="_blank" rel="noopener noreferrer" className="break-all font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800" {...props}>{children}</a>
                  : <span>{children}</span>
            }}>{message.content}</ReactMarkdown>
          </div>
        )}
      </div>
    </motion.article>
  );
}

function SuggestionCard({ suggestion, onClick, disabled }) {
  const Icon = suggestion.icon;
  return (
    <button type="button" disabled={disabled} onClick={() => onClick(suggestion.prompt)} className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5 text-left transition-colors hover:border-emerald-200 hover:bg-emerald-50/30 disabled:cursor-not-allowed disabled:opacity-50">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600"><Icon /></div>
      <div className="min-w-0 flex-1"><p className="text-[13px] font-semibold text-slate-800">{suggestion.title}</p><p className="mt-1 text-[11px] leading-relaxed text-slate-500">{suggestion.description}</p></div>
      <FiArrowRight className="shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

export default function Assistant() {
  const { user, profile, activeFirm, activeClient, isLoading: workspaceLoading, workspaceError } = useClient();
  const userId = user?.id || null;
  const clientId = activeClient?.id || null;
  const organizationName = activeClient?.name || null;
  const canUseAssistant = Boolean(userId && clientId && !workspaceLoading && !workspaceError);

  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState('');
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showQuickBooksImport, setShowQuickBooksImport] = useState(false);
  const [quickBooksAccounts, setQuickBooksAccounts] = useState([]);
  const [profitYoAccounts, setProfitYoAccounts] = useState([]);
  const [isLoadingQuickBooksAccounts, setIsLoadingQuickBooksAccounts] = useState(false);
  const [quickBooksError, setQuickBooksError] = useState(null);
  const [showWaveImport, setShowWaveImport] = useState(false);
  const [waveAccounts, setWaveAccounts] = useState([]);
  const [isLoadingWaveAccounts, setIsLoadingWaveAccounts] = useState(false);
  const [waveError, setWaveError] = useState(null);

  const textareaRef = useRef(null);
  const chatScrollRef = useRef(null);
  const importContainerRef = useRef(null);
  const requestVersionRef = useRef(0);
  const importVersionRef = useRef(0);
  const requestPendingRef = useRef(false);
  const mountedRef = useRef(false);
  const scopeKey = userId && clientId ? `${userId}:${clientId}` : null;
  const currentScopeRef = useRef(scopeKey);
  currentScopeRef.current = scopeKey;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestVersionRef.current += 1;
      importVersionRef.current += 1;
    };
  }, []);

  useEffect(() => {
    requestVersionRef.current += 1;
    importVersionRef.current += 1;
    requestPendingRef.current = false;
    setMessages([]);
    setQuery('');
    setError('');
    setIsThinking(false);
    setShowImportMenu(false);
    setShowCsvImport(false);
    setShowQuickBooksImport(false);
    setQuickBooksAccounts([]);
    setProfitYoAccounts([]);
    setQuickBooksError(null);
    setIsLoadingQuickBooksAccounts(false);
    setShowWaveImport(false);
    setWaveAccounts([]);
    setWaveError(null);
    setIsLoadingWaveAccounts(false);
  }, [scopeKey]);

  useEffect(() => {
    const container = chatScrollRef.current;
    if (!container) return;
    const frame = requestAnimationFrame(() => { container.scrollTop = container.scrollHeight; });
    return () => cancelAnimationFrame(frame);
  }, [messages, isThinking]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, [query, messages.length, clientId]);

  useEffect(() => {
    if (!showImportMenu) return;
    const outside = (event) => {
      if (importContainerRef.current && !importContainerRef.current.contains(event.target)) setShowImportMenu(false);
    };
    const escape = (event) => { if (event.key === 'Escape') setShowImportMenu(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [showImportMenu]);

  const handleSend = async (messageOverride = null) => {
    const text = String(messageOverride ?? query).trim();
    if (!text || !canUseAssistant || requestPendingRef.current) return;
    if (text.length > MAX_MESSAGE_LENGTH) {
      setError(`Messages must be ${MAX_MESSAGE_LENGTH} characters or fewer.`);
      return;
    }
    const requestScope = scopeKey;
    const requestId = ++requestVersionRef.current;
    const previousHistory = messages.slice(-MAX_HISTORY).map(({ role, content }) => ({ role, content }));
    requestPendingRef.current = true;
    setMessages((previous) => [...previous, makeMessage('user', text)]);
    setQuery('');
    setError('');
    setIsThinking(true);
    try {
      const result = await aiService.askAssistant(text, previousHistory, user, profile, activeClient);
      if (!mountedRef.current || requestVersionRef.current !== requestId || currentScopeRef.current !== requestScope) return;
      if (typeof result?.message !== 'string' || !result.message.trim()) throw new Error('ProfitYO AI returned an empty response.');
      setMessages((previous) => [...previous, makeMessage('assistant', result.message)]);
    } catch (requestError) {
      if (!mountedRef.current || requestVersionRef.current !== requestId || currentScopeRef.current !== requestScope) return;
      console.error('ProfitYO AI request failed:', requestError);
      setError(requestError?.message || 'Unable to process your request. Please try again.');
    } finally {
      if (mountedRef.current && requestVersionRef.current === requestId && currentScopeRef.current === requestScope) {
        requestPendingRef.current = false;
        setIsThinking(false);
      }
    }
  };

  const resetChat = () => {
    requestVersionRef.current += 1;
    requestPendingRef.current = false;
    setMessages([]);
    setQuery('');
    setError('');
    setIsThinking(false);
    setShowImportMenu(false);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void handleSend();
    }
  };

  
const handleQuickBooksImport = () => {
  if (!canUseAssistant || !clientId) return;

  setShowImportMenu(false);
  setQuickBooksError(null);
  setError('');

  setShowQuickBooksImport(true);
};


  const handleWaveImport = async () => {
    if (!canUseAssistant) return;
    const importScope = scopeKey;
    const importId = ++importVersionRef.current;
    setShowImportMenu(false);
    setWaveError(null);
    setIsLoadingWaveAccounts(true);
    try {
      const result = await getWaveAccounts(clientId);
      if (!mountedRef.current || importVersionRef.current !== importId || currentScopeRef.current !== importScope) return;
      if (!Array.isArray(result?.accounts)) throw new Error('Wave did not return a valid account list.');
      setWaveAccounts(result.accounts);
      setShowWaveImport(true);
    } catch (requestError) {
      if (!mountedRef.current || importVersionRef.current !== importId || currentScopeRef.current !== importScope) return;
      console.error('Wave account lookup failed:', requestError);
      const message = requestError?.message || 'Unable to load Wave accounts.';
      setWaveError(message);
      setError(message);
    } finally {
      if (mountedRef.current && importVersionRef.current === importId && currentScopeRef.current === importScope) setIsLoadingWaveAccounts(false);
    }
  };

  const handleImportSelection = (type) => {
    if (!canUseAssistant) return;
    setShowImportMenu(false);
    setError('');
    if (type === 'csv') setShowCsvImport(true);
    else if (type === 'quickbooks') void handleQuickBooksImport();
    else if (type === 'wave') void handleWaveImport();
  };

  // The backend must enforce selectedAccountIds and mappings before this is enabled.
  const handleQuickBooksSelection = () => {
    const message = 'QuickBooks import is in review-only mode. The backend must verify selected accounts and mappings before changes can be saved.';
    setQuickBooksError(message);
    setError(message);
    return { success: false, error: message };
  };

  const handleWaveSelection = () => {
    const message = 'Wave account selection is available, but the import operation still needs to be connected to its verified Supabase import service. No import has been confirmed.';
    setWaveError(message);
    setError(message);
    return { success: false, error: message };
  };

  const renderImportMenu = () => (
    <AnimatePresence>
      {showImportMenu && (
        <motion.div key="import-menu" initial={{ opacity: 0, y: 6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 6, scale: 0.98 }} transition={{ duration: 0.15 }} className="absolute bottom-[calc(100%+10px)] left-0 z-50 w-[min(320px,calc(100vw-3rem))] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-100 px-4 py-3"><p className="text-[13px] font-semibold text-slate-900">Import Data</p><p className="mt-1 text-[11px] text-slate-500">Choose an available import source.</p></div>
          <div className="p-2">
            {IMPORT_OPTIONS.map((option) => {
              const Icon = option.icon;
              const loading = option.id === 'quickbooks' ? isLoadingQuickBooksAccounts : option.id === 'wave' ? isLoadingWaveAccounts : false;
              return (
                <button key={option.id} type="button" disabled={loading || !canUseAssistant} onClick={() => handleImportSelection(option.id)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50 disabled:opacity-50">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">{loading ? <FiLoader className="animate-spin" /> : <Icon />}</div>
                  <div className="min-w-0"><p className="text-[12px] font-semibold text-slate-800">{option.label}</p><p className="mt-0.5 text-[11px] text-slate-500">{option.description}</p></div>
                </button>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const renderComposer = () => (
    <div className="w-full">
      <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm transition-colors focus-within:border-slate-300">
        <div className="flex items-end gap-2">
          <div ref={importContainerRef} className="relative shrink-0">
            {renderImportMenu()}
            <button type="button" onClick={() => setShowImportMenu((previous) => !previous)} disabled={!canUseAssistant} aria-label="Import accounting data" aria-expanded={showImportMenu} title="Import data" className={`flex h-10 w-10 items-center justify-center rounded-xl transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${showImportMenu ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}><FiPlus className="text-lg" /></button>
          </div>
          <textarea ref={textareaRef} rows={1} maxLength={MAX_MESSAGE_LENGTH} value={query} disabled={!canUseAssistant} onChange={(event) => setQuery(event.target.value)} onKeyDown={handleKeyDown} placeholder={canUseAssistant ? `Ask about ${organizationName}...` : 'Select a client workspace to begin...'} aria-label="Message ProfitYO AI" className="max-h-[120px] min-h-[40px] min-w-0 flex-1 resize-none overflow-y-auto bg-transparent px-1 py-2.5 text-[14px] leading-5 text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:opacity-60" />
          <button type="button" onClick={() => void handleSend()} disabled={!canUseAssistant || !query.trim() || isThinking} aria-label="Send message" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400">{isThinking ? <FiLoader className="animate-spin" /> : <FiArrowUp />}</button>
        </div>
        <div className="flex items-center justify-between gap-3 px-2 pb-1 pt-2"><span className="text-[10px] text-slate-400">Enter to send · Shift + Enter for a new line</span><span className="shrink-0 text-[10px] text-slate-400">{query.length}/{MAX_MESSAGE_LENGTH}</span></div>
      </div>
      <p className="mt-2 text-center text-[10px] leading-relaxed text-slate-400">AI responses may contain errors. Verify important accounting and financial information.</p>
    </div>
  );

  const hasMessages = messages.length > 0;
  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-white">
      <CsvImportModal isOpen={showCsvImport && Boolean(clientId)} onClose={() => setShowCsvImport(false)} organizationId={clientId} userId={userId} organizationName={organizationName || ''} />
            
      <QuickBooksImportModal
        isOpen={
          showQuickBooksImport &&
          Boolean(clientId)
        }
        onClose={() => {
          setShowQuickBooksImport(false);
          setQuickBooksError(null);
        }}
        organizationId={clientId}
        organizationName={organizationName || ''}
        externalError={quickBooksError}
        importContractReady={false}
      />

      <WaveImportModal isOpen={showWaveImport && Boolean(clientId)} onClose={() => { setShowWaveImport(false); setWaveError(null); }} accounts={waveAccounts} organizationId={clientId} organizationName={organizationName || ''} loading={isLoadingWaveAccounts} externalError={waveError} profitYoAccounts={[]} onImport={handleWaveSelection} />

      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white"><FiCpu className="text-lg" /></div>
          <div className="min-w-0"><h1 className="text-[15px] font-semibold text-slate-900">ProfitYO AI</h1><p className="truncate text-[11px] text-slate-500">{organizationName ? `Accounting copilot · ${organizationName}` : 'Your accounting copilot'}</p></div>
        </div>
        <button type="button" onClick={resetChat} disabled={!hasMessages && !query && !error} className="flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40"><FiPlus />New Chat</button>
      </header>

      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {workspaceLoading ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-5 text-center"><FiLoader className="animate-spin text-2xl text-slate-500" /><p className="text-sm text-slate-500">Loading your workspace...</p></div>
        ) : !clientId ? (
          <div className="flex flex-1 flex-col items-center justify-center px-5 py-10 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-600"><FiBriefcase className="text-2xl" /></div>
            <h2 className="mt-5 text-xl font-semibold text-slate-900">Select a client to get started</h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-500">{activeFirm ? `Your firm, ${activeFirm.name}, is ready. Select a client workspace to ask questions about its financial records.` : 'An authorized client workspace is required to use the accounting copilot.'}</p>
            {workspaceError && <div role="alert" className="mt-5 max-w-lg rounded-xl border border-rose-100 bg-rose-50 p-3 text-xs text-rose-700">{workspaceError}</div>}
          </div>
        ) : !hasMessages ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center px-5 py-8 sm:px-8">
              <div className="mx-auto max-w-xl text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><FiCpu className="text-xl" /></div>
                <h2 className="mt-5 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">How can I help with your accounting?</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">Ask questions about available financial records, transactions, and reports for <span className="font-semibold text-slate-700">{organizationName}</span>.</p>
              </div>
              <div className="mt-9 grid grid-cols-1 gap-3 sm:grid-cols-2">{SUGGESTIONS.map((suggestion) => <SuggestionCard key={suggestion.title} suggestion={suggestion} disabled={!canUseAssistant || isThinking} onClick={(prompt) => void handleSend(prompt)} />)}</div>
              <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-slate-400"><FiShield />Questions use the selected client workspace</div>
            </div>
            <div className="mx-auto w-full max-w-3xl shrink-0 px-4 pb-5 pt-3 sm:px-8">
              {error && <div role="alert" className="mb-3 flex items-start gap-2 rounded-xl border border-rose-100 bg-rose-50 p-3 text-xs text-rose-700"><FiAlertCircle className="mt-0.5 shrink-0" /><span>{error}</span></div>}
              {renderComposer()}
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <section ref={chatScrollRef} aria-label="AI conversation" className="assistant-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-6 sm:px-8">
              <div aria-live="polite" className="mx-auto flex w-full max-w-5xl flex-col gap-5">
                <AnimatePresence initial={false}>{messages.map((message) => <MessageBubble key={message.id} message={message} />)}</AnimatePresence>
                {isThinking && <div className="flex items-center gap-3"><div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white"><FiCpu /></div><div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500"><FiLoader className="animate-spin" />Analyzing available data...</div></div>}
                {error && <div role="alert" className="flex items-start gap-2 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-xs text-rose-700"><FiAlertCircle className="mt-0.5 shrink-0" /><span>{error}</span></div>}
              </div>
            </section>
            <footer className="shrink-0 border-t border-slate-100 bg-white px-4 pb-4 pt-3 sm:px-8">
              <div className="mx-auto w-full max-w-3xl"><p className="mb-2 flex items-center justify-center gap-2 text-[11px] text-slate-500"><FiBriefcase className="shrink-0" /><span className="truncate">Current client: {organizationName}</span></p>{renderComposer()}</div>
            </footer>
          </div>
        )}
      </main>
    </div>
  );
}